import { ErrorBoundary } from '@src/components/ErrorBoundary';
import { AppRoutes } from '@src/containers/Routes';
import { EndpointsContextProvider } from '@src/providers/EndpointsContextProvider';
import { UserContextProvider } from '@src/providers/UserContextProvider';
import { GlobalStyle } from '@src/styles/GlobalStyle';
import { ThemeProvider } from '@src/styles/ThemeProvider';
import { envConfig } from '@src/utils/envConfig';
import {
  AuthContextProvider,
  BaseStyle,
  getWsClient,
  PortalContextProvider,
  shouldForwardStyledProp,
  usePreferredTheme,
  WebSocketClientProvider,
} from '@talos/kyoko';
import { useEffect, useState } from 'react';
import { StyleSheetManager } from 'styled-components';
import { STYLE_TAG_ID } from './components/Layout/tokens';
import { useThemeTypePreference } from './hooks/useThemeTypePreference';
import { AppInstallPromptCaptureProvider } from './providers/AppInstallPromptProvider';
import { OrgConfigurationProvider } from './providers/OrgConfigurationProvider';

export default function App() {
  const { themeType } = useThemeTypePreference();
  const preferredTheme = usePreferredTheme('DarkTalosTheme', themeType);
  const [stylesTarget, setStylesTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const el = document.getElementById(STYLE_TAG_ID);
    if (el) {
      setStylesTarget(document.getElementById(STYLE_TAG_ID));
    }
  }, []);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', preferredTheme.backgroundContent);
    }
  }, [preferredTheme]);

  return (
    // Keep this above auth/user providers so beforeinstallprompt is captured before Android Chrome drops it.
    <AppInstallPromptCaptureProvider>
      <ThemeProvider theme={preferredTheme}>
        <BaseStyle />
        <GlobalStyle />
        <div id={STYLE_TAG_ID} />
        {stylesTarget && (
          <StyleSheetManager shouldForwardProp={shouldForwardStyledProp} target={stylesTarget}>
            <ErrorBoundary>
              <PortalContextProvider>
                <AuthContextProvider
                  loginUrl={envConfig.VITE_AUTH_LOGIN_URL}
                  logoutUrl={envConfig.VITE_AUTH_LOGOUT_URL}
                  sessionCookieName={envConfig.VITE_SESSION_COOKIE}
                  rememberOriginalUrl={true}
                >
                  {/* Note: This User Context Provider should remain above the WebSocketClientProvider - if not, see note in WebSocketClientProvider */}
                  <UserContextProvider>
                    <EndpointsContextProvider>
                      <WebSocketClientProvider
                        wsClient={getWsClient()}
                        enableDatadogSubscriptionStats={true}
                        enableDatadogWebSocketDisconnectMetric={true}
                      >
                        <OrgConfigurationProvider>
                          <AppRoutes />
                        </OrgConfigurationProvider>
                      </WebSocketClientProvider>
                    </EndpointsContextProvider>
                  </UserContextProvider>
                </AuthContextProvider>
              </PortalContextProvider>
            </ErrorBoundary>
          </StyleSheetManager>
        )}
      </ThemeProvider>
    </AppInstallPromptCaptureProvider>
  );
}
